/**
 * Карта «Сделок».
 *
 * Показывает ровно то же множество, что и список: точки приходят тем же
 * серверным отбором, только с координатами. Догадок по строке адреса здесь нет
 * вовсе — на карту попадает лишь подтверждённая точка, потому что придуманная
 * координата выглядит как настоящая и отправит курьера не туда.
 *
 * Выбор общий со списком: клик по маркеру меняет тот же самый набор.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../ui/components';
import type { MapConfig } from '../routing/geo';
import {
  depotAbsenceReason,
  depotMarkerOf,
  visiblePoints,
  type DepotView,
  type MapPoint,
  type TimeWindow,
} from './deals-view';
import { parseTimeFilter, selectionNumber } from './selection';
import {
  clearTimeFilter,
  parseTimeFilter as parseStoredTimeFilter,
  readTimeFilter,
  timeFilterStorageKey,
  writeTimeFilter,
} from './time-filter-storage';

/**
 * Карта грузится отдельным куском: MapLibre и разбор тайлов — сотни килобайт,
 * и остальным экранам они не нужны.
 */
const DealsMapCanvas = lazy(() =>
  import('./DealsMapCanvas').then((module) => ({ default: module.DealsMapCanvas })),
);

/**
 * Форма слова «заказ» после числа в родительном падеже.
 *
 * «У 1 заказов» читается как опечатка и подрывает доверие к самому числу,
 * поэтому счётчик согласуется с ним, а не обходится безличным «шт.».
 */
function ordersWord(count: number): string {
  const tail = count % 100;
  return tail % 10 === 1 && tail !== 11 ? 'заказа' : 'заказов';
}

export type DealPoint = MapPoint;

interface MapResponse {
  points: DealPoint[];
  deliveryDate: string;
}

interface DealsMapProps {
  scopeKey: string;
  selected: readonly string[];
  /** Отдаёт саму точку: пригодность берётся из неё, а не из списка. */
  onToggle: (point: MapPoint) => void;
  /** Левая панель скрыта — карта занимает всю ширину. */
  listHidden: boolean;
  onToggleList: () => void;
  /**
   * Сколько заказов дня осталось без подтверждённой координаты.
   *
   * Число считает список — тем же серверным отбором, что и карта. Карта его
   * только называет: пустая карта без причины выглядит как поломка, а не как
   * незаконченная работа с адресами.
   */
  withoutPoint: number;
  /** Увести логиста к первому заказу, адрес которого и чинится. */
  onFixAddresses: () => void;
}

/**
 * Порог кластеризации в градусах.
 *
 * Близкие невыбранные точки на дальнем масштабе сливаются в одну отметку
 * с количеством: два десятка маркеров на одном доме нечитаемы. Выбранные
 * в кластер не попадают никогда — их номер обязан оставаться видимым.
 */
const CLUSTER_STEP = 0.01;

export interface Cluster {
  key: string;
  points: DealPoint[];
}

export function clusterize(points: readonly DealPoint[]): Cluster[] {
  const buckets = new Map<string, DealPoint[]>();
  for (const point of points) {
    const key = `${Math.round(Number(point.lat) / CLUSTER_STEP)}:${Math.round(
      Number(point.lon) / CLUSTER_STEP,
    )}`;
    buckets.set(key, [...(buckets.get(key) ?? []), point]);
  }
  return [...buckets.entries()].map(([key, items]) => ({ key, points: items }));
}

/**
 * Состояние маркера.
 *
 * Различается и цветом, и формой: одного цвета мало тому, кто его не различает,
 * а маркер без различий превращает карту в набор одинаковых точек.
 */
export type MarkerState = 'FREE' | 'PICKED' | 'DRAFT' | 'ASSEMBLED';

export interface MarkerLook {
  color: string;
  shape: 'circle' | 'number' | 'ring' | 'check';
}

/**
 * Склада среди состояний нет: на этой карте отметки склада не существует,
 * а легенда, называющая то, чего не показывают, хуже отсутствующей.
 */
export const MARKER_LOOKS: Record<MarkerState, MarkerLook> = {
  FREE: { color: '#6b7280', shape: 'circle' },
  PICKED: { color: 'hsl(210 70% 45%)', shape: 'number' },
  DRAFT: { color: '#9ca3af', shape: 'ring' },
  ASSEMBLED: { color: 'hsl(150 55% 32%)', shape: 'check' },
};

/**
 * Разделяет точки на выбранные и кластеризуемые.
 *
 * Выбранная точка в кластер не попадает никогда: её номер — часть маршрута,
 * который человек уже спланировал, и прятать его в безымянную группу нельзя.
 */
export function splitForMap(
  points: readonly DealPoint[],
  selected: readonly string[],
  zoomedOut: boolean,
): { chosen: DealPoint[]; clusters: Cluster[] } {
  const selectedSet = new Set(selected);
  const chosen = points.filter((point) => selectedSet.has(point.orderId));
  const rest = points.filter((point) => !selectedSet.has(point.orderId));
  return {
    chosen,
    clusters: zoomedOut
      ? clusterize(rest)
      : rest.map((point) => ({ key: point.orderId, points: [point] })),
  };
}

export function DealsMap({
  scopeKey,
  selected,
  onToggle,
  listHidden,
  onToggleList,
  withoutPoint,
  onFixAddresses,
}: DealsMapProps): React.JSX.Element {
  const { client, user } = useAuth();
  const userId = user?.id ?? null;
  /*
   * По умолчанию точки показываются ПООТДЕЛЬНОСТИ.
   *
   * Группировка удобна на обзорном масштабе, но логист приходит на карту
   * работать с конкретными заказами: свёрнутая группа скрывает и номер,
   * и готовность, и заставляет первым делом нажимать «Показать отдельно».
   */
  const [zoomedOut, setZoomedOut] = useState(false);
  // Черновик полей времени и ПРИМЕНЁННЫЕ значения. Фильтрует карту только
  // применённое: набор в поле сам по себе ничего не пересчитывает. Так поведение
  // одинаково во всех браузерах — `input type="time"` шлёт `change` по-разному
  // (кто-то на каждую цифру, кто-то по завершении ввода).
  // Начальные значения берём из сохранённого применённого фильтра ЭТОГО
  // пользователя: он переживает переходы, перезагрузку и повторный вход.
  const [from, setFrom] = useState(() => readTimeFilter(userId).from);
  const [to, setTo] = useState(() => readTimeFilter(userId).to);
  const [appliedFrom, setAppliedFrom] = useState(() => readTimeFilter(userId).from);
  const [appliedTo, setAppliedTo] = useState(() => readTimeFilter(userId).to);
  const applyTimeFilter = useCallback((): void => {
    setAppliedFrom(from);
    setAppliedTo(to);
    // Сохраняем ТОЛЬКО применённое значение, а не черновик.
    writeTimeFilter(userId, { from, to });
  }, [from, to, userId]);
  const resetTimeFilter = useCallback((): void => {
    setFrom('');
    setTo('');
    setAppliedFrom('');
    setAppliedTo('');
    clearTimeFilter(userId);
  }, [userId]);

  // Синхронизация между вкладками: применение или сброс в другой вкладке этого
  // же пользователя доходит сюда без перезагрузки. Событие `storage` приходит
  // только из ДРУГИХ вкладок, поэтому эхо-защита не нужна.
  useEffect(() => {
    const key = timeFilterStorageKey(userId);
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== key) {
        return;
      }
      const next = parseStoredTimeFilter(event.newValue);
      setFrom(next.from);
      setTo(next.to);
      setAppliedFrom(next.from);
      setAppliedTo(next.to);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);
  const [basemapFailed, setBasemapFailed] = useState(false);

  const query = useQuery({
    queryKey: ['deals-map', scopeKey],
    queryFn: () => client.get<MapResponse>(`/api/deals/map?${scopeKey}`),
    // Смена фильтра меняет ключ запроса, то есть создаёт НОВЫЙ запрос без
    // данных. Без этого карта на время загрузки размонтировалась бы целиком
    // и создавалась заново — логист видел бы моргание вместо обновления.
    placeholderData: keepPreviousData,
  });

  /*
   * Основной склад.
   *
   * Маршрут начинается со склада, поэтому логист обязан видеть его на той же
   * карте, что и заказы. Запрос отдельный и редкий: склады меняются гораздо
   * реже, чем заказы дня.
   */
  const depots = useQuery({
    queryKey: ['depots'],
    queryFn: () => client.get<{ items: DepotView[] }>('/api/depots'),
    staleTime: 60 * 1000,
  });

  // Подложка своя: адрес стиля приходит из нашей конфигурации, публичные тайлы
  // не используются.
  const config = useQuery({
    queryKey: ['map-config'],
    queryFn: () => client.get<MapConfig>('/api/map/config'),
    staleTime: 5 * 60 * 1000,
  });

  /*
   * Последние успешно полученные данные.
   *
   * `keepPreviousData` держит прежний ответ, пока новый ЗАГРУЖАЕТСЯ, но если
   * повторный запрос УПАЛ, данных у запроса не остаётся. Карта из-за временной
   * ошибки исчезать не должна, поэтому последний удачный ответ помнится
   * отдельно и переживает любой отказ обновления.
   */
  const lastGood = useRef<MapResponse | null>(null);
  if (query.data !== undefined) {
    lastGood.current = query.data;
  }
  const shown = query.data ?? lastGood.current;

  // Адрес стиля запоминается так же: пропасть он может только вместе с картой.
  const lastStyleUrl = useRef('');
  const configuredStyleUrl = config.data?.styleUrl ?? '';
  if (configuredStyleUrl !== '') {
    lastStyleUrl.current = configuredStyleUrl;
  }

  const all = useMemo(() => shown?.points ?? [], [shown]);

  /*
   * Фильтр времени карты.
   *
   * Ограничивает ТОЛЬКО показанные отметки. Список заказов слева при этом
   * не меняется и не перезагружается: это отдельный, более узкий вопрос
   * «что я сейчас вижу на карте», а не смена рабочего отбора дня.
   */
  // Карта фильтруется по ПРИМЕНЁННЫМ значениям, а не по черновику полей.
  const fromMinute = parseTimeFilter(appliedFrom);
  const toMinute = parseTimeFilter(appliedTo);
  const points = useMemo(
    () => visiblePoints(all, { fromMinute, toMinute } satisfies TimeWindow),
    [all, fromMinute, toMinute],
  );
  const hidden = all.length - points.length;

  const { chosen, clusters } = splitForMap(points, selected, zoomedOut);

  // Номер выбранного заказа подписью на отметке. Пусто — заказ не выбран.
  const numberOf = useCallback(
    (orderId: string): number | null => selectionNumber(selected, orderId),
    [selected],
  );

  // Состояние загрузки допустимо ТОЛЬКО до первых данных. Дальше карта
  // остаётся на месте при любом обновлении.
  if (shown === null && query.isPending) {
    return <LoadingState title="Загружаем карту…" />;
  }
  if (shown === null) {
    return <ErrorState title="Не удалось загрузить карту" onRetry={() => void query.refetch()} />;
  }

  const styleUrl = lastStyleUrl.current;
  const empty = points.length === 0;
  // Обновление идёт, но прежние точки показаны: это не загрузка, а уточнение.
  const refreshing = query.isFetching;
  // Обновление не удалось. Карта и прежние точки остаются: они всё ещё верны,
  // просто новее ничего не пришло.
  const refreshFailed = query.isError;

  return (
    <section className="deals-map" data-testid="deals-map">
      {depots.data !== undefined && depotAbsenceReason(depots.data.items) !== null && (
        <p className="deals-map__notice-line" role="status" data-testid="deals-map-no-depot">
          {depotAbsenceReason(depots.data.items)}: маршрут не с чего начинать. Координаты не
          угадываются.
        </p>
      )}

      {refreshFailed && (
        <p className="deals-map__refresh-error" role="status" data-testid="deals-map-refresh-error">
          Не удалось обновить точки. Показаны прежние.{' '}
          <button type="button" className="deals__link" onClick={() => void query.refetch()}>
            Повторить
          </button>
        </p>
      )}

      {/*
        Карта показывается ВСЕГДА, даже когда точек ноль. Пустой день — обычное
        дело, и подложка Москвы в этот момент нужна не меньше: логист видит,
        где он работает, а не серый прямоугольник. Сообщение об отсутствии
        координат лежит поверх карты и её не заменяет.
      */}
      {/*
        Шапка карты — настоящая строка над холстом, а не контролы поверх него.

        Плавающие панели закрывали ту часть карты, на которую и смотрят: точки
        в верхней трети оказывались под счётчиком и полями времени. Теперь
        сверху то, что описывает карту, ниже — чем управлять её показом, а сам
        холст начинается там, где заканчивается управление.
      */}
      <div className="deals-map__head">
        <div className="deals-map__head-row">
          <span className="deals-map__head-count" data-testid="deals-map-head-count">
            На карте: {points.length}
            {hidden > 0 && <span className="deals-map__muted"> · скрыто фильтром: {hidden}</span>}
            {refreshing && (
              <span className="deals-map__refreshing" data-testid="deals-map-refreshing">
                {' '}
                Обновляем…
              </span>
            )}
          </span>
          <span className="deals-map__head-hint">Фильтр времени — только для карты</span>
          <div className="deals-map__head-time">
            {/*
              Кнопка «Применить» слева от полей: значения времени вступают в силу
              только по ней или по Enter внутри поля. До этого карта показывает
              прежний подтверждённый диапазон.
            */}
            <button
              type="button"
              className="deals__link deals-map__time-apply"
              data-testid="deals-map-apply"
              onClick={applyTimeFilter}
            >
              Применить
            </button>
            {(appliedFrom !== '' || appliedTo !== '') && (
              <button
                type="button"
                className="deals__link deals-map__time-reset"
                data-testid="deals-map-reset"
                onClick={resetTimeFilter}
              >
                Сбросить
              </button>
            )}
            {/*
          Счётчик и его пояснения занимают постоянное место: иначе поля времени
          и кнопка группировки прыгали бы при каждом фоновом обновлении.
        */}
            {/*
          Два простых поля времени. Набор значения ничего не пересчитывает —
          фильтр применяется отдельно, кнопкой или по Enter.
        */}
            {/*
              Подписи «От» и «До» скрыты от глаз, но не от программы чтения.
              
              Между двумя полями времени и так стоит тире — оно называет
              диапазон короче любого слова, а два слова рядом с узкими полями
              занимали больше места, чем сами поля. Без подписи поле осталось
              бы безымянным для того, кто его не видит, поэтому текст никуда
              не делся — он только перестал печататься.
            */}
            <label className="deals-map__time">
              <span className="visually-hidden">Время от</span>
              <input
                type="time"
                value={from}
                data-testid="deals-map-from"
                onChange={(event) => setFrom(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    applyTimeFilter();
                  }
                }}
              />
            </label>
            <span className="deals-map__time-dash" aria-hidden="true">
              –
            </span>
            <label className="deals-map__time">
              <span className="visually-hidden">Время до</span>
              <input
                type="time"
                value={to}
                data-testid="deals-map-to"
                onChange={(event) => setTo(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    applyTimeFilter();
                  }
                }}
              />
            </label>
          </div>
        </div>

        <div className="deals-map__head-row deals-map__head-row--actions">
          <button
            type="button"
            className="deals-map__panel-toggle deals-map__panel-toggle--accent"
            aria-expanded={!listHidden}
            aria-controls="deals-column"
            data-testid="deals-toggle-list"
            onClick={onToggleList}
          >
            {listHidden ? 'Показать список' : 'Скрыть список'}
          </button>
          <button
            type="button"
            className="deals__link deals-map__zoom"
            data-testid="deals-map-zoom"
            // Разделять нечего, пока точек нет: кнопка не должна обещать
            // действие, которое ничего не изменит.
            disabled={empty}
            onClick={() => setZoomedOut((value) => !value)}
          >
            {zoomedOut ? 'Показать отдельно' : 'Сгруппировать'}
          </button>
        </div>
      </div>

      <div className="deals-map__surface">
        {/*
          Управление картой лежит ПОВЕРХ холста.

          Отдельная полоса над картой отнимала у неё высоту и разрывала рабочую
          поверхность на разрозненные ряды. Карта — фон всей правой панели,
          а её контролы плавают над ней.
        */}
        {/*
          Управление картой двумя плавающими строками.

          Сверху — что показано: счётчик и легенда. Ниже — чем это менять:
          время и группировка. Обе строки лежат ПОВЕРХ холста и высоту у карты
          не отнимают.
        */}
        {/*
          Состояние подложки — отдельный факт, а не замена подсказке.

          Прежде оно вытесняло собой объяснение пустой карты: логист видел
          «Карта не настроена» и не узнавал ни того, что у заказов дня нет
          подтверждённых точек, ни того, что с этим делать. Подложка и адреса
          чинятся разными людьми и в разных местах.
        */}
        {styleUrl === '' ? (
          <p className="deals-map__notice" role="status" data-testid="deals-map-notice">
            Карта не настроена
          </p>
        ) : basemapFailed ? (
          <p className="deals-map__notice" role="status" data-testid="deals-map-notice">
            Подложка карты не загрузилась. Список и выбор работают как обычно.
          </p>
        ) : (
          <Suspense fallback={<LoadingState title="Готовим карту…" />}>
            <DealsMapCanvas
              styleUrl={styleUrl}
              attribution={config.data?.attribution ?? null}
              chosen={chosen}
              clusters={clusters}
              depot={depotMarkerOf(depots.data?.items ?? [])}
              numberOf={numberOf}
              onToggle={(orderId) => {
                const point = points.find((item) => item.orderId === orderId);
                if (point !== undefined) {
                  onToggle(point);
                }
              }}
              onLoadError={() => setBasemapFailed(true)}
            />
          </Suspense>
        )}

        {/*
          Пустая карта объясняет себя сама.

          Прежняя строка «нет заказов с координатами» называла следствие и
          молчала о причине: логист видел серое поле и не знал, сломалось ли
          что-то, не тот ли выбран день или работа с адресами просто не
          доделана. Причин ровно три, и они требуют разного, поэтому блок
          говорит про ту, которая случилась, и даёт к ней действие.
        */}
        {empty && (
          <div className="deals-map__empty" role="status" data-testid="deals-map-empty">
            <h3 className="deals-map__empty-title">Ни одного заказа на карте</h3>
            {all.length > 0 ? (
              <>
                <p className="deals-map__empty-text">
                  Точки дня есть — все {all.length} скрыты фильтром времени. Список слева фильтр не
                  трогает.
                </p>
                <button
                  type="button"
                  className="deals-map__empty-action"
                  data-testid="deals-map-empty-action"
                  onClick={resetTimeFilter}
                >
                  Показать все часы
                </button>
              </>
            ) : withoutPoint > 0 ? (
              <>
                <p className="deals-map__empty-text">
                  На карту попадает только подтверждённая точка. У {withoutPoint}{' '}
                  {ordersWord(withoutPoint)} дня её нет: адрес не указан, неполон или разошёлся с
                  МоимСкладом.
                </p>
                <button
                  type="button"
                  className="deals-map__empty-action deals-map__empty-action--accent"
                  data-testid="deals-map-empty-action"
                  onClick={onFixAddresses}
                >
                  Исправить адреса · {withoutPoint}
                </button>
              </>
            ) : (
              <p className="deals-map__empty-text">
                В выбранном дне заказов нет. Карта остаётся на месте вместе со складом: с него
                начинается любой маршрут.
              </p>
            )}
          </div>
        )}
      </div>

      {/*
        Легенда — подпись под картой, а не накладка на ней.

        Поверх холста она закрывала собой нижний край и спорила с точками,
        которые объясняет: подпись должна стоять рядом с картинкой,
        а не на ней.
      */}
      <ul className="deals-map__legend" data-testid="deals-map-legend">
        <li>
          <span className="deals-map__dot deals-map__dot--free" /> доступен
        </li>
        <li>
          <span className="deals-map__dot deals-map__dot--picked" /> выбран
        </li>
        <li>
          <span className="deals-map__dot deals-map__dot--draft" /> в черновике
        </li>
        <li>
          <span className="deals-map__dot deals-map__dot--assembled" /> собран
        </li>
        <li>
          <span className="deals-map__dot deals-map__dot--depot" /> склад
        </li>
      </ul>
    </section>
  );
}
